import { Badge, Button } from '@aaas/shared';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { ReviewStatus } from '../types';

interface ReviewStatusButtonsProps {
  status: ReviewStatus;
  onStatusChange: (status: ReviewStatus) => void;
  size?: 'sm' | 'default';
}

export function ReviewStatusButtons({ status, onStatusChange, size = 'sm' }: ReviewStatusButtonsProps) {
  const { t } = useTranslation('ats');
  const isSmall = size === 'sm';
  const btnSize = isSmall ? 'sm' : 'default';
  const iconSize = isSmall ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <div className="flex items-center gap-1">
      <Button
        variant={status === 'pass' ? 'default' : 'outline'}
        size={btnSize}
        onClick={(e) => {
          e.stopPropagation();
          onStatusChange(status === 'pass' ? null : 'pass');
        }}
        className={
          status === 'pass'
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'hover:bg-green-50 hover:text-green-700 hover:border-green-300'
        }
      >
        <Check className={iconSize} />
        {!isSmall && <span className="ml-1">{t('reviewStatus.pass')}</span>}
      </Button>
      <Button
        variant={status === 'fail' ? 'default' : 'outline'}
        size={btnSize}
        onClick={(e) => {
          e.stopPropagation();
          onStatusChange(status === 'fail' ? null : 'fail');
        }}
        className={
          status === 'fail'
            ? 'bg-red-600 hover:bg-red-700 text-white'
            : 'hover:bg-red-50 hover:text-red-700 hover:border-red-300'
        }
      >
        <X className={iconSize} />
        {!isSmall && <span className="ml-1">{t('reviewStatus.fail')}</span>}
      </Button>
    </div>
  );
}

export function ReviewStatusBadge({ status }: { status: ReviewStatus }) {
  const { t } = useTranslation('ats');
  if (!status) return null;

  return (
    <Badge
      variant={status === 'pass' ? 'default' : 'destructive'}
      className={
        status === 'pass'
          ? 'bg-green-100 text-green-800 hover:bg-green-100'
          : 'bg-red-100 text-red-800 hover:bg-red-100'
      }
    >
      {status === 'pass' ? t('reviewStatus.pass') : t('reviewStatus.fail')}
    </Badge>
  );
}
